import { APP_NAME, SUPPORT_EMAIL } from "../app.constants";

export interface SeoPage {
  title: string;
  eyebrow: string;
  description: string;
  sections: { title: string; text: string }[];
}

export const SEO_TOPIC_PAGES: Record<string, SeoPage> = {
  "anonymous-chat": {
    eyebrow: "PRIVATE BY DEFAULT",
    title: `Anonymous chat online — no account, no sign up required`,
    description: `Have a free anonymous chat with strangers online. ${APP_NAME} never asks for your email or real name — pick a nickname and start talking instantly.`,
    sections: [
      {
        title: `A nickname is all you need`,
        text: `${APP_NAME} never asks for an email address, phone number, or social login. Choose any display name you like — real or completely made up — and you are ready to start. There is no profile to fill out, no verification step, and no identity linked to your session.`,
      },
      {
        title: `No message history — conversations disappear when you leave`,
        text: `${APP_NAME} does not store your messages on its servers. Once the session ends, the conversation is gone. Your chat history lives only in your own browser and clearing your browser data removes it entirely. Nothing is tied to an account because there is no account.`,
      },
      {
        title: `Block and move on with one tap`,
        text: "If a conversation feels uncomfortable or disrespectful, you are never stuck. The Block button is always available in the chat and takes effect immediately — that person will not appear in your matches for the rest of your session. Skip is also available at any point for a softer exit.",
      },
    ],
  },
  "practice-english": {
    eyebrow: "LANGUAGE PRACTICE",
    title: "Practice English with new people",
    description:
      "Build confidence in English by having relaxed conversations with people around the world.",
    sections: [
      {
        title: "Real people, real conversations",
        text: "Practise English with native speakers and learners from around the world in a pressure-free one-to-one chat.",
      },
      {
        title: "No judgment",
        text: "Anonymous chat means you can make mistakes without embarrassment. Every conversation is a fresh start.",
      },
      {
        title: "Choose language interests",
        text: "Add 'English learning' or 'languages' as an interest to match with people who are happy to help.",
      },
    ],
  },
  "make-friends-online": {
    eyebrow: "FIND YOUR PEOPLE",
    title: `Make friends online — meet people who share your interests`,
    description: `Make new friends online through interest-based anonymous text chat on ${APP_NAME}. Meet real people from around the world, no account required.`,
    sections: [
      {
        title: `Shared interests are the best conversation starters`,
        text: `The friendships that last usually start with something in common — a hobby, a favourite band, a shared curiosity about the world. ${APP_NAME} matches you with people who share your listed interests so you always have a natural opening line. Add the topics that matter to you and let the algorithm do the initial filtering.`,
      },
      {
        title: `One-to-one, at your own pace`,
        text: `Every conversation on ${APP_NAME} is private, one-to-one, and completely at your pace. You are never in a group chat or performing for an audience. Skip anyone you do not connect with in one tap and stay as long as you like with the ones you do. There is no social pressure and no timeline of posts to keep up with.`,
      },
      {
        title: `Meet people from every corner of the world`,
        text: `${APP_NAME} connects people across time zones and borders. The person you are matched with next could be in your city or on the other side of the planet. That global reach means the community is always active and the chance of finding a genuine connection is always there, whenever you decide to chat.`,
      },
    ],
  },
  "voice-chat-with-strangers": {
    eyebrow: "CONVERSATION",
    title: `Voice chat with strangers`,
    description: `Use ${APP_NAME} to find new conversations and get to know people beyond profiles and follower counts.`,
    sections: [
      {
        title: `Start with text today`,
        text: `${APP_NAME} is currently a text-chat platform. Anonymous conversations are available right now, no download needed.`,
      },
      {
        title: `Voice is on the roadmap`,
        text: "Voice features are planned for a future version. Join the community now so you are ready when they launch.",
      },
      {
        title: `Interest-driven matching`,
        text: `Whether text or voice, ${APP_NAME} always tries to connect you with people who share your interests.`,
      },
    ],
  },
  "text-chat-with-strangers": {
    eyebrow: "TEXT CHAT",
    title: "Text chat with strangers",
    description:
      "Enjoy a free anonymous text conversation with a random match, with interests to help break the ice.",
    sections: [
      {
        title: "Instant connection",
        text: "A match is found in seconds. Type your first message and the conversation is underway.",
      },
      {
        title: "Interests as icebreakers",
        text: "Shared interests appear at the top of every chat so you always have something to talk about.",
      },
      {
        title: "Full privacy",
        text: "No account, no real name, no stored history. The anonymity is built in from the start.",
      },
    ],
  },
  "video-chat-with-strangers": {
    eyebrow: "MEET LIVE",
    title: "Video chat with strangers",
    description:
      "Discover new people online and choose the pace and format of every conversation. Stay in control from the first hello.",
    sections: [
      {
        title: `Text chat is available now`,
        text: `${APP_NAME} is a text-first platform today. Start anonymous text conversations instantly, no camera required.`,
      },
      {
        title: `Stay in control`,
        text: "Skip, block, or leave any conversation at any time. You decide how much to share and for how long.",
      },
      {
        title: "Global community",
        text: "Meet people from over 190 countries in a respectful, interest-guided chat environment.",
      },
    ],
  },
};
